import { api } from '../../client';

export const ACCESS_CONTROL_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'accounting', label: 'Accounting & Finance' },
  { key: 'sales', label: 'Sales & CRM' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'hr', label: 'HR & Payroll' },
  { key: 'projects', label: 'Projects' },
  { key: 'reports', label: 'Reports' },
  { key: 'settings', label: 'Settings' },
] as const;

export type ModuleKey = (typeof ACCESS_CONTROL_MODULES)[number]['key'];

export interface ModulePermissions {
  view?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
}

export interface RoleDto {
  id: string;
  name: string;
  status: string;
  description: string;
  permissions: Record<string, ModulePermissions>;
  created_at?: string;
  updated_at?: string;
}

export interface UserOptionDto {
  id: string;
  name: string;
  email: string;
}

export interface UserRoleAssignmentDto {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  role_id: string;
  role_name: string;
  status: string;
  restricted_modules: string[];
  created_at?: string;
  updated_at?: string;
}

export const accessControlApi = {
  async listRoles(): Promise<{ data?: RoleDto[]; message?: string }> {
    return api.get('/access-control/roles') as Promise<{ data: RoleDto[] }>;
  },

  async getRole(id: string): Promise<{ data?: RoleDto; message?: string }> {
    return api.get(`/access-control/roles/${id}`) as Promise<{ data: RoleDto }>;
  },

  async createRole(data: {
    name: string;
    status?: string;
    description?: string;
    permissions?: Record<string, ModulePermissions>;
  }): Promise<{ data?: RoleDto; message?: string; errors?: Record<string, string[]> }> {
    return api.post('/access-control/roles', data) as Promise<{
      data: RoleDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async updateRole(
    id: string,
    data: {
      name?: string;
      status?: string;
      description?: string;
      permissions?: Record<string, ModulePermissions>;
    }
  ): Promise<{ data?: RoleDto; message?: string; errors?: Record<string, string[]> }> {
    return api.put(`/access-control/roles/${id}`, data) as Promise<{
      data: RoleDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async deleteRole(id: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/access-control/roles/${id}`) as Promise<{ success: boolean; message?: string }>;
  },

  async listAssignments(): Promise<{ data?: UserRoleAssignmentDto[]; message?: string }> {
    return api.get('/access-control/assignments') as Promise<{ data: UserRoleAssignmentDto[] }>;
  },

  async createUserAndAssign(data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    role_id: string;
    status?: string;
    restricted_modules?: string[];
  }): Promise<{
    data?: UserRoleAssignmentDto;
    message?: string;
    errors?: Record<string, string[]>;
  }> {
    return api.post('/access-control/create-user-and-assign', data) as Promise<{
      data: UserRoleAssignmentDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async createAssignment(data: {
    user_id: string;
    role_id: string;
    status?: string;
    restricted_modules?: string[];
  }): Promise<{
    data?: UserRoleAssignmentDto;
    message?: string;
    errors?: Record<string, string[]>;
  }> {
    return api.post('/access-control/assignments', data) as Promise<{
      data: UserRoleAssignmentDto;
      message?: string;
      errors?: Record<string, string[]>;
    }>;
  },

  async updateAssignment(
    id: string,
    data: { status?: string; restricted_modules?: string[] }
  ): Promise<{ data?: UserRoleAssignmentDto; message?: string }> {
    return api.put(`/access-control/assignments/${id}`, data) as Promise<{
      data: UserRoleAssignmentDto;
      message?: string;
    }>;
  },

  async deleteAssignment(id: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/access-control/assignments/${id}`) as Promise<{ success: boolean; message?: string }>;
  },

  async listUsers(): Promise<{ data?: UserOptionDto[]; message?: string }> {
    return api.get('/access-control/users') as Promise<{ data: UserOptionDto[] }>;
  },
};
